import { useMemo } from 'react';
import { Table } from 'antd';
import type { CSSProperties } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import type { ShipmentListType } from 'schemas/shipment/inbound/list';
import { INBOUND_SHIPMENT_COLUMN_DEFS } from '@utility/constants/inbound-shipment-columns';
import {
  inboundShipmentDetailPath,
  resolveInboundListDisplay,
} from '@utility/utils/shipment-display';
import { COLOR } from '@styles/theme';
import { renderInboundShipmentCell } from './inboundShipmentCells';

const NO_COLUMN_DIVIDER: CSSProperties = {
  borderInlineEnd: 'none',
};

const CELL_STYLE: CSSProperties = {
  ...NO_COLUMN_DIVIDER,
  verticalAlign: 'middle',
  paddingTop: 16,
  paddingBottom: 16,
};

type InboundShipmentTableProps = {
  data: ShipmentListType[];
  loading: boolean;
  totalData: number;
  filter: { page?: number; limit?: number };
  pageChanged: (page: number) => void;
  filterChanged: (key: string, value: unknown) => void;
};

export function InboundShipmentTable({
  data,
  loading,
  totalData,
  filter,
  pageChanged,
  filterChanged,
}: InboundShipmentTableProps) {
  const navigate = useNavigate();

  const columns: ColumnsType<ShipmentListType> = useMemo(
    () =>
      INBOUND_SHIPMENT_COLUMN_DEFS.map(col => ({
        key: col.key,
        title: col.title,
        width: col.width,
        dataIndex: col.key,
        onHeaderCell: () => ({
          style: { ...NO_COLUMN_DIVIDER, verticalAlign: 'middle' },
        }),
        onCell: () => ({ style: CELL_STYLE }),
        render: (_: unknown, record: ShipmentListType) =>
          renderInboundShipmentCell(col.key, record, resolveInboundListDisplay(record)),
      })),
    [],
  );

  const pagination: TablePaginationConfig = {
    current: filter.page ?? 1,
    pageSize: filter.limit ?? 10,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [5, 10, 20],
    showTotal: total => `${total} shipments`,
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <Table<ShipmentListType>
      rowKey="_id"
      columns={columns}
      dataSource={data}
      loading={loading}
      size="middle"
      tableLayout="fixed"
      pagination={pagination}
      onRow={record => ({
        onClick: () => navigate(inboundShipmentDetailPath(record._id)),
        style: { cursor: 'pointer' },
      })}
      style={{
        background: COLOR.white,
        borderRadius: 6,
        border: `1px solid ${COLOR.border}`,
        overflow: 'hidden',
      }}
    />
  );
}
